/**
 * Customer health signals — pure and dependency-free.
 *
 * Shared by the admin app (client-side preview of a customer's health) and
 * the Trigger.dev `health-score-recompute` task so both always produce the
 * same numbers from the same inputs.
 *
 * Design rules
 * ────────────
 * • Every signal is an integer 0–20 (matching the INTEGER `*_signal` columns
 *   on `customer_health_scores`) or `null` when its inputs are unavailable.
 * • `null` never becomes a silent "20" — a missing input removes the signal
 *   from the score instead of inventing a good result.
 * • `score` is normalised (0–100) over the signals that DO have data, and is
 *   `null` when nothing has data. `coverage` tells the caller how much of the
 *   picture the score is based on.
 * • `mrr_signal` is always `null`: there is no MRR history table yet.
 *
 * Thresholds (documented once here; keep the tests in
 * tests/health-signals.test.ts in sync when you change them)
 * ────────────────────────────────────────────────────────────
 * tickets_signal  — open tickets (any status not resolved/closed)
 *                    0–2 → 20 · 3–5 → 10 · 6+ → 0
 * sla_signal      — open tickets already past `tickets.sla_deadline`
 *                    0 → 20 · 1 → 5 · 2+ → 0
 *                    Any breach also forces status = 'critical'.
 * uptime_signal   — worst of the available components (the signal answers
 *                    "is anything down?", so the weakest component wins):
 *                    · uptimePct24h (location_uptime_samples, last 24h mean)
 *                        ≥ 99.5 → 20 · ≥ 98 → 15 · ≥ 95 → 10 · ≥ 90 → 5 · < 90 → 0
 *                    · openAlerts (network_alerts, unresolved)
 *                        0 → 20 · 1–2 → 10 · 3+ → 0
 *                    · deployments (only when deploymentsTotal > 0)
 *                        any offline → 0 · any degraded → 10 · otherwise → 20
 *                    null when all three components are null.
 * renewal_signal  — days until `customers.contract_renewal_date`
 *                    > 90 → 20 · 31–90 → 10 · ≤ 30 (incl. overdue) → 0
 * mrr_signal      — always null (no MRR snapshots table).
 *
 * Status (aligned with `healthRingColor` in @pazportal/ui so the ring colour
 * and the status label can never disagree):
 *   score null            → 'unknown'
 *   breachedTickets > 0   → 'critical'
 *   score < 50            → 'critical'
 *   score < 75            → 'degraded'
 *   any signal === 0      → 'degraded'  (a fully failed dimension is never "healthy")
 *   otherwise             → 'healthy'
 */
export type HealthStatus = 'healthy' | 'degraded' | 'critical' | 'unknown';

export interface HealthSignalInput {
  /** Open tickets (open / in_progress / waiting_on_customer). null = unknown. */
  openTickets: number | null;
  /** Open tickets whose `sla_deadline` is already in the past. null = unknown. */
  breachedTickets: number | null;
  /** Mean `online_pct` from location_uptime_samples over the last 24h. null = no samples. */
  uptimePct24h: number | null;
  /** Unresolved network alerts. null = no alert source connected. */
  openAlerts: number | null;
  /** Days until contract renewal (negative = overdue). null = no renewal date. */
  renewalDays: number | null;
  /** Deployments with status 'offline'. null = unknown. */
  deploymentsOffline: number | null;
  /** Deployments with status 'degraded' (or 'installing'). null = unknown. */
  deploymentsDegraded: number | null;
  /** Deployments counted (excluding decommissioned). 0 or null = no deployment data. */
  deploymentsTotal: number | null;
}

export interface HealthSignalResult {
  tickets_signal: number | null;
  sla_signal: number | null;
  uptime_signal: number | null;
  renewal_signal: number | null;
  /** Always null until an MRR history table exists. */
  mrr_signal: null;
  /** 0–100 normalised over the non-null signals; null when no signal has data. */
  score: number | null;
  /**
   * Share of signals with data, 0–1, over all HEALTH_SIGNAL_KEYS (5).
   * Because mrr_signal is always null the ceiling is currently 0.8;
   * render it as "N of 5 signals" rather than as a percentage.
   */
  coverage: number;
  status: HealthStatus;
}

/** Maximum value of a single signal. */
export const HEALTH_SIGNAL_MAX = 20;

/** The five signal columns on customer_health_scores, in display order. */
export const HEALTH_SIGNAL_KEYS = [
  'tickets_signal',
  'sla_signal',
  'uptime_signal',
  'renewal_signal',
  'mrr_signal',
] as const;

export type HealthSignalKey = (typeof HEALTH_SIGNAL_KEYS)[number];

/** Score thresholds shared with the UI ring colour. */
export const HEALTH_SCORE_HEALTHY_MIN = 75;
export const HEALTH_SCORE_DEGRADED_MIN = 50;

// ─── Input sanitising ────────────────────────────────────────────────────────

/** Finite number or null. Counts are clamped at 0; nothing else is invented. */
function count(value: number | null | undefined): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return value < 0 ? 0 : value;
}

/** Finite number or null (negatives allowed — overdue renewals are negative). */
function signed(value: number | null | undefined): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return value;
}

/** Finite percentage clamped to 0–100, or null. */
function percentage(value: number | null | undefined): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  return Math.min(100, Math.max(0, value));
}

// ─── Individual signals ──────────────────────────────────────────────────────

export function ticketsSignal(openTickets: number | null | undefined): number | null {
  const n = count(openTickets);
  if (n === null) return null;
  return n <= 2 ? 20 : n <= 5 ? 10 : 0;
}

export function slaSignal(breachedTickets: number | null | undefined): number | null {
  const n = count(breachedTickets);
  if (n === null) return null;
  return n === 0 ? 20 : n === 1 ? 5 : 0;
}

export function renewalSignal(renewalDays: number | null | undefined): number | null {
  const d = signed(renewalDays);
  if (d === null) return null;
  return d > 90 ? 20 : d > 30 ? 10 : 0;
}

function uptimePctComponent(uptimePct24h: number | null | undefined): number | null {
  const pct = percentage(uptimePct24h);
  if (pct === null) return null;
  if (pct >= 99.5) return 20;
  if (pct >= 98) return 15;
  if (pct >= 95) return 10;
  if (pct >= 90) return 5;
  return 0;
}

function alertsComponent(openAlerts: number | null | undefined): number | null {
  const n = count(openAlerts);
  if (n === null) return null;
  return n === 0 ? 20 : n <= 2 ? 10 : 0;
}

function deploymentsComponent(
  offline: number | null | undefined,
  degraded: number | null | undefined,
  total: number | null | undefined,
): number | null {
  const t = count(total);
  const off = count(offline);
  const deg = count(degraded);
  if (t === null || t === 0 || off === null || deg === null) return null;
  return off > 0 ? 0 : deg > 0 ? 10 : 20;
}

export function uptimeSignal(input: {
  uptimePct24h: number | null | undefined;
  openAlerts: number | null | undefined;
  deploymentsOffline: number | null | undefined;
  deploymentsDegraded: number | null | undefined;
  deploymentsTotal: number | null | undefined;
}): number | null {
  const components = [
    uptimePctComponent(input.uptimePct24h),
    alertsComponent(input.openAlerts),
    deploymentsComponent(
      input.deploymentsOffline,
      input.deploymentsDegraded,
      input.deploymentsTotal,
    ),
  ].filter((c): c is number => c !== null);
  if (components.length === 0) return null;
  return Math.min(...components);
}

// ─── Composite ───────────────────────────────────────────────────────────────

export function computeHealthSignals(input: HealthSignalInput): HealthSignalResult {
  const tickets_signal = ticketsSignal(input.openTickets);
  const sla_signal = slaSignal(input.breachedTickets);
  const uptime_signal = uptimeSignal(input);
  const renewal_signal = renewalSignal(input.renewalDays);
  const mrr_signal = null;

  const signals: Record<HealthSignalKey, number | null> = {
    tickets_signal,
    sla_signal,
    uptime_signal,
    renewal_signal,
    mrr_signal,
  };

  const present = HEALTH_SIGNAL_KEYS.map((k) => signals[k]).filter(
    (v): v is number => v !== null,
  );

  const score =
    present.length === 0
      ? null
      : Math.round(
          (present.reduce((sum, v) => sum + v, 0) / (present.length * HEALTH_SIGNAL_MAX)) * 100,
        );

  const coverage = present.length / HEALTH_SIGNAL_KEYS.length;

  const breached = count(input.breachedTickets) ?? 0;

  let status: HealthStatus;
  if (score === null) status = 'unknown';
  else if (breached > 0) status = 'critical';
  else if (score < HEALTH_SCORE_DEGRADED_MIN) status = 'critical';
  else if (score < HEALTH_SCORE_HEALTHY_MIN) status = 'degraded';
  else if (present.some((v) => v === 0)) status = 'degraded';
  else status = 'healthy';

  return {
    tickets_signal,
    sla_signal,
    uptime_signal,
    renewal_signal,
    mrr_signal,
    score,
    coverage,
    status,
  };
}
